import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Container,
  Row,
  Col,
  Table,
  Alert,
  Badge,
} from "reactstrap";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Accordion, AccordionDetails, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import newApi from "service/newApi";

import ModalAttendanceAnalysisPrompt from "components/Modal/ModalAttendanceAnalysisPrompt";
import ModalAttendanceAnalysisResponse from "components/Modal/ModalAttendanceAnalysisResponse";
import ModalAttendanceAnalysisFeedback from "components/Modal/ModalAttendanceAnalysisFeedback";
import ModalLibraryMatchingTerms from "components/Modal/ModalLibraryMatchingTerms";
import CopyableText from "components/CopyableText";
import AudioPlayer from "components/AudioPlayer";
import Loading from "components/Loading";

import { AccordionSummary } from "assets/css/custom-components";
import { styledTableScroll } from "assets/css/custom-components";
import { tableHeaderStyle } from "assets/css/custom-components";
import { tableContentStyle } from "assets/css/custom-components";

import { DateTool } from "utils/DateTool";

const libraryMatchType = {
  EXACT_MATCH: "Exatidão",
  SIMILARITY: "Similaridade",
  LOCATION: "Localização",
};

const libraryMatchSpeaker = {
  0: "Cliente",
  1: "Consultor",
};

const Attendance = () => {
  const { id: attendanceId } = useParams();

  const [loading, setLoading] = useState(false);

  const [attendance, setAttendance] = useState({});
  const [attendanceName, setAttendanceName] = useState("");
  const [messages, setMessages] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [libraryMatches, setLibraryMatches] = useState([]);

  const [updateAttendance, setUpdateAttendance] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [orderPromptsBy, setOrderPromptsBy] = useState([]);

  const questionRegex = /<pergunta>(.*?)<pergunta>/;

  useEffect(() => {
    setLoading(true);

    newApi
      .get(`/v1/attendances/${attendanceId}`)
      .then((response) => response.data)
      .then((response) => {
        setAttendance(response ?? {});
        setAttendanceName(response?.attendance ?? "");
        setMessages(response.messages ?? []);
        setPrompts(response.prompts ?? []);
        setLibraryMatches(response.matches ?? []);
        setTotalPrice(
          response.prompts.reduce((acc, curr) => acc + curr.price, 0),
        );
        setLoading(false);
      })
      .catch(() => {
        toast.error("Erro ao recuperar as análises do atendimento!");
        setLoading(false);
      });
  }, [attendanceId]);

  useEffect(() => {
    if (updateAttendance) {
      setLoading(true);
      newApi
        .get(`/v1/attendances/${attendanceId}`)
        .then((response) => response.data)
        .then((response) => {
          setAttendance(response ?? {});
          setMessages(response.messages ?? []);
          setPrompts(response.prompts ?? []);
          setTotalPrice(
            response.prompts.reduce((acc, curr) => acc + curr.price, 0),
          );
          setLoading(false);
          setUpdateAttendance(false);
        })
        .catch(() => {
          toast.error("Erro ao recuperar as análises do atendimento!");
          setLoading(false);
          setUpdateAttendance(false);
        });
    }
  }, [updateAttendance]);

  return (
    <>
      <Container className="mt-4" fluid style={{ marginBottom: "100px" }}>
        <Col xl="12">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">
                    <b>Atendimento:</b>{" "}
                    <CopyableText label="Atendimento" text={attendanceName} />
                  </h3>
                </Col>
                <Col className="text-right" xs="4"></Col>
              </Row>
            </CardHeader>
            <CardBody>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <Alert color="primary">
                    Foram processados{" "}
                    <b>
                      {prompts?.filter((p) => p.type === "AI").length ?? 0}{" "}
                      prompt(s)
                    </b>{" "}
                    para o atendimento com o valor total de{" "}
                    <b>${totalPrice ?? 0} USD</b>
                  </Alert>
                </Col>
              </div>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <FormGroup>
                    <label className="form-control-label">
                      <b>Áudio</b>
                    </label>
                    <div>
                      {!loading && (
                        <AudioPlayer
                          attendanceId={attendanceId}
                          filename={attendanceName}
                          source={attendance?.source}
                          folderId={attendance?.folderId}
                          audio={attendance?.audio}
                          origin={attendance?.origin}
                        />
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </div>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <FormGroup style={{ backgroundColor: "aliceblue" }}>
                    <Accordion
                      defaultExpanded
                      sx={{
                        "&.Mui-expanded": {
                          paddingBottom: "12px",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="info-content"
                        id="info-header"
                      >
                        <label
                          className="form-control-label"
                          style={{ marginBottom: 0 }}
                        >
                          <b>Informações do atendimento</b>
                        </label>
                      </AccordionSummary>
                      <Divider />
                      <Col className="pl-lg-12 pt-2">
                        <div>
                          <label className="form-control-label">
                            Segmento:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance?.segment?.title ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          <label className="form-control-label">
                            Processado por:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.user?.username ?? "N/A"}
                            </span>
                          </label>
                          <br />

                          <label className="form-control-label">
                            Supervisor:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.supervisorExternalId ?? "N/A"}
                            </span>
                          </label>
                          <br />

                          <label className="form-control-label">
                            Consultor:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.consultantExternalId ?? "N/A"}
                            </span>
                          </label>
                        </div>
                      </Col>
                    </Accordion>
                  </FormGroup>
                </Col>
              </div>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <FormGroup>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="transcription-content"
                        id="transcription-header"
                      >
                        <label
                          className="form-control-label"
                          style={{ marginBottom: 0 }}
                        >
                          <b>Transcrição</b>{" "}
                          {messages.length > 0 && (
                            <Badge
                              color="primary"
                              style={{ fontSize: "12px" }}
                              onClick={(e) => e.preventDefault()}
                            >
                              ({messages.length})
                            </Badge>
                          )}
                        </label>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div
                          className="styled-scroll"
                          style={styledTableScroll}
                        >
                          {loading ? <Loading /> : ""}
                          <Table
                            style={{
                              tableLayout: "fixed",
                              width: "100%",
                            }}
                          >
                            <thead className="thead-ligth"></thead>
                            <tbody>
                              {messages.map((message, i) => {
                                let rowStyle = {};

                                const escapeHtml = (text) => {
                                  return text
                                    .replace(/&/g, "&amp;")
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;")
                                    .replace(/"/g, "&quot;")
                                    .replace(/'/g, "&#x27;");
                                };

                                let formattedMessage = escapeHtml(message);

                                if (message.includes("OPERADOR")) {
                                  rowStyle = { backgroundColor: "#e3f2fd" };
                                  formattedMessage = formattedMessage.replace(
                                    /(OPERADOR)/g,
                                    function (match) {
                                      return "<b>" + match + "</b>";
                                    },
                                  );
                                } else if (message.includes("CLIENTE")) {
                                  rowStyle = { backgroundColor: "#fff" };
                                  formattedMessage = formattedMessage.replace(
                                    /(CLIENTE)/g,
                                    function (match) {
                                      return "<b>" + match + "</b>";
                                    },
                                  );
                                }

                                formattedMessage = formattedMessage.replace(
                                  /&lt;([^&]+)&gt;/g,
                                  function (match, content) {
                                    return "<b>&lt;" + content + "&gt;</b>";
                                  },
                                );

                                return (
                                  <tr key={i} style={rowStyle}>
                                    <td>
                                      <div
                                        style={{
                                          width: "100%",
                                          whiteSpace: "pre-line",
                                          wordWrap: "break-word",
                                          overflowWrap: "break-word",
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: formattedMessage,
                                        }}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </FormGroup>
                </Col>
              </div>
              <Col className="pl-lg-12">
                <FormGroup>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="analysis-content"
                      id="analysis-header"
                    >
                      <label
                        className="form-control-label"
                        style={{ marginBottom: 0 }}
                      >
                        <b>Análises Generativas</b>{" "}
                        {prompts.length > 0 && (
                          <Badge
                            color="primary"
                            style={{ fontSize: "12px" }}
                            onClick={(e) => e.preventDefault()}
                          >
                            ({prompts.length})
                          </Badge>
                        )}
                      </label>
                    </AccordionSummary>
                    <div className="styled-scroll" style={styledTableScroll}>
                      {loading ? <Loading /> : ""}
                      <Table
                        style={{
                          tableLayout: "fixed",
                          width: "100%",
                        }}
                      >
                        <thead className="thead-ligth">
                          <tr className="custom-head">
                            <th
                              scope="col"
                              title="Prompt"
                              onClick={() =>
                                setOrderPromptsBy([
                                  "prompt",
                                  orderPromptsBy[1] === "desc" ? "asc" : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Prompt{" "}
                              {orderPromptsBy.length &&
                              orderPromptsBy[0] === "prompt"
                                ? orderPromptsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                            <th
                              scope="col"
                              title="Resposta"
                              onClick={() =>
                                setOrderPromptsBy([
                                  "response",
                                  orderPromptsBy[1] === "desc" ? "asc" : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Resposta{" "}
                              {orderPromptsBy.length &&
                              orderPromptsBy[0] === "response"
                                ? orderPromptsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                            <th
                              scope="col"
                              title="Checklist"
                              onClick={() =>
                                setOrderPromptsBy([
                                  "checklist",
                                  orderPromptsBy[1] === "desc" ? "asc" : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Checklist{" "}
                              {orderPromptsBy.length &&
                              orderPromptsBy[0] === "checklist"
                                ? orderPromptsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                            <th
                              scope="col"
                              title="Feedback/Ações"
                              style={tableHeaderStyle}
                            >
                              Feedback / Ações
                            </th>
                            <th
                              scope="col"
                              title="Preço"
                              onClick={() =>
                                setOrderPromptsBy([
                                  "price",
                                  orderPromptsBy[1] === "desc" ? "asc" : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Preço ( USD ){" "}
                              {orderPromptsBy.length &&
                              orderPromptsBy[0] === "price"
                                ? orderPromptsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                            <th
                              scope="col"
                              title="Horário de análise"
                              onClick={() =>
                                setOrderPromptsBy([
                                  "date",
                                  orderPromptsBy[1] === "desc" ? "asc" : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Horário de análise{" "}
                              {orderPromptsBy.length &&
                              orderPromptsBy[0] === "date"
                                ? orderPromptsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {prompts
                            .filter((prompt) => prompt.type !== "HUMAN")
                            .sort((a, b) => {
                              if (orderPromptsBy.length) {
                                let firstData = a;
                                let secondData = b;

                                switch (orderPromptsBy[0]) {
                                  case "type":
                                    firstData = a.type;
                                    secondData = b.type;
                                    break;
                                  case "prompt":
                                    firstData = a.prompt;
                                    secondData = b.prompt;
                                    break;
                                  case "response":
                                    firstData = a.response;
                                    secondData = b.response;
                                    break;
                                  case "checklist":
                                    firstData = a.checklist;
                                    secondData = b.checklist;
                                    break;
                                  case "price":
                                    firstData = (a.price * 10000).toString();
                                    secondData = (b.price * 10000).toString();
                                    break;
                                }

                                if (orderPromptsBy[0] === "date") {
                                  firstData = new Date(a.createdAt);
                                  secondData = new Date(b.createdAt);
                                  if (orderPromptsBy[1] === "desc") {
                                    return firstData - secondData;
                                  }
                                  return secondData - firstData;
                                }

                                const questionA = firstData?.replace(
                                  questionRegex,
                                  "$1",
                                );
                                const questionB = secondData?.replace(
                                  questionRegex,
                                  "$1",
                                );

                                let numA = questionA.match(/\d+/g);
                                let numB = questionB.match(/\d+/g);

                                numA = numA ? parseInt(numA[0], 10) : 0;
                                numB = numB ? parseInt(numB[0], 10) : 0;

                                if (orderPromptsBy[1] === "desc") {
                                  if (numA === numB) {
                                    return questionA.localeCompare(questionB);
                                  } else {
                                    return numA - numB;
                                  }
                                }
                                if (numA === numB) {
                                  return questionB.localeCompare(questionA);
                                } else {
                                  return numB - numA;
                                }
                              }

                              return true;
                            })
                            .map((prompt, i) => {
                              return (
                                <tr key={i}>
                                  <td
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    <ModalAttendanceAnalysisPrompt
                                      id={i}
                                      question={prompt.prompt}
                                      prompt={prompt.prompt}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    <ModalAttendanceAnalysisResponse
                                      response={prompt.response}
                                    />
                                  </td>
                                  <td>{prompt.checklist}</td>
                                  <td>
                                    <ModalAttendanceAnalysisFeedback
                                      attendance={prompt.attendance}
                                      analysisId={prompt.id}
                                      promptId={prompt.promptId}
                                    />
                                  </td>
                                  <td title={prompt.price}>${prompt.price}</td>
                                  <td title={DateTool.format(prompt.createdAt)}>
                                    {DateTool.format(prompt.createdAt)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </div>
                  </Accordion>
                  <div className="mt-4">
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="matching-content"
                        id="matching-header"
                      >
                        <label
                          className="form-control-label"
                          style={{ marginBottom: 0 }}
                        >
                          <b>Enquadramento De Bibliotecas</b>{" "}
                          {libraryMatches.length > 0 && (
                            <Badge
                              color="primary"
                              style={{ fontSize: "12px" }}
                              onClick={(e) => e.preventDefault()}
                            >
                              ({libraryMatches.length})
                            </Badge>
                          )}
                        </label>
                      </AccordionSummary>
                      <Divider />
                      <div className="styled-scroll" style={styledTableScroll}>
                        {loading ? <Loading /> : ""}
                        <Table
                          style={{
                            tableLayout: "fixed",
                            width: "100%",
                          }}
                        >
                          <thead className="thead-ligth">
                            <tr className="custom-head">
                              <th
                                scope="col"
                                title="Biblioteca"
                                style={tableHeaderStyle}
                              >
                                Biblioteca
                              </th>
                              <th
                                scope="col"
                                title="Tipo"
                                style={tableHeaderStyle}
                              >
                                Tipo
                              </th>
                              <th
                                scope="col"
                                title="Agente"
                                style={tableHeaderStyle}
                              >
                                Agente
                              </th>
                              <th
                                scope="col"
                                title="Termos"
                                style={tableHeaderStyle}
                              >
                                Termos
                              </th>
                              <th
                                scope="col"
                                title="Enquadramentos"
                                style={tableHeaderStyle}
                              >
                                Enquadramentos
                              </th>
                              <th
                                scope="col"
                                title="Horário de enquadramento"
                                style={tableHeaderStyle}
                              >
                                Horário de enquadramento
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {libraryMatches.map((match, i) => {
                              return (
                                <tr key={i} className="custom-tr">
                                  <td
                                    title={match.libraryTitle}
                                    style={tableContentStyle}
                                  >
                                    {match.libraryTitle}
                                  </td>
                                  <td>{libraryMatchType[match.matchType]}</td>
                                  <td>{libraryMatchSpeaker[match.speaker]}</td>
                                  <td>
                                    <ModalLibraryMatchingTerms
                                      matchType={
                                        libraryMatchType[match.matchType]
                                      }
                                      terms={match.terms}
                                    />
                                  </td>
                                  <td>{match.totalTerms}</td>
                                  <td
                                    title={DateTool.format(match.createdAt)}
                                    style={tableContentStyle}
                                  >
                                    {DateTool.format(match.createdAt)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </Accordion>
                  </div>
                </FormGroup>
              </Col>
            </CardBody>
          </Card>
        </Col>
      </Container>
    </>
  );
};

export default Attendance;
